import { useEffect, useState, useRef } from 'react'
import { thumb } from '../lib/img'
import { usePhone } from '../lib/device'
import { useLocation } from 'react-router-dom'
import { useNav } from '../lib/nav'
import { Page, TopBar } from '../components/Shell'
import { I } from '../components/Icons'
import { NumPad } from '../components/NumPad'
import { useStore } from '../state/store'
import { PriceHint } from './Sell'
import { TradeAside } from './Trade'
import { loadTradeLimits } from '../lib/api'
import { fmtC, lineCents, ownerLabel, type TradeLimits } from '../lib/types'

/** Change the agreed price on a piece they take. It came in at list; this is only for a different agreed amount. */
export function TradePrice() {
  const nav = useNav()
  const { trade, setTrade } = useStore()
  const start = (useLocation().state as { step?: number } | null)?.step ?? 0
  const [step, setStep] = useState(Math.min(start, Math.max(0, trade.out.length - 1)))
  const [edited, setEdited] = useState<Record<number, boolean>>({})
  const [limits, setLimits] = useState<TradeLimits | null>(null)
  useEffect(() => { loadTradeLimits().then(setLimits).catch(() => {}) }, [])
  const l = trade.out[step] as typeof trade.out[number] | undefined
  // Motion: a step's mark turns into a tick that draws on when its piece gets a price; the ones priced when the screen opened sit still
  const pricedAtOpen = useRef(new Set(trade.out.filter((x) => lineCents(x) > 0).map((x) => x.piece.variant_id)))
  useEffect(() => { if (!l) nav('/trade', { replace: true }) }, [l, nav])
  if (!l) return null
  const setPrice = (price: string) => { setEdited((e) => ({ ...e, [step]: true })); setTrade((t) => ({ out: t.out.map((x, i) => i === step ? { ...x, price } : x) })) }
  const allPriced = trade.out.every((x) => lineCents(x) > 0)
  const phone = usePhone()
  return (
    <Page
      bar={<TopBar back="Trade" backTo="/trade" title="Price what they take" caption={`Piece ${step + 1} of ${trade.out.length}`} />}
      aside={<TradeAside cta={() => nav('/trade')} ctaLabel="Back to the trade" ctaOff={!allPriced} limits={limits} showMethod={false} />}
    >
      <div className="steps">
        {trade.out.map((x, i) => (
          <button key={x.piece.variant_id} type="button" className={'step' + (i === step ? ' on' : '')} onClick={() => setStep(i)}>
            <span className="step__mark">{lineCents(x) > 0 ? <span className={pricedAtOpen.current.has(x.piece.variant_id) ? '' : 'step__mark--in'} style={{ display: 'flex' }}>{I.tick(15, 'currentColor')}</span> : i + 1}</span><span>{x.piece.brand}</span>
          </button>
        ))}
      </div>
      <div className="pricegrid">
        <div className="card piece">
          <div className="piece__img">{l.piece.image_url ? <img src={l.piece.image_url} alt="" /> : <span className="hint">No photo in Shopify</span>}</div>
          <div className="piece__brand">{l.piece.brand}</div>
          <div className="piece__title">{l.piece.title}</div>
          <div className="card review-rows" style={{ background: 'var(--page)' }}>
            <div className="row"><span className="row__label">List price</span><span className="row__value">{fmtC(l.piece.list_cents)}</span></div>
            <div className="row"><span className="row__label">Floor</span><span className="row__value">{l.piece.floor_cents != null ? fmtC(l.piece.floor_cents) : 'No floor set'}</span></div>
            <div className="row"><span className="row__label">Whose piece</span><span className="row__value">{ownerLabel(l.piece.owner, l.piece.consignor_key)}</span></div>
            <div className="row"><span className="row__label">SKU</span><span className="row__value">{l.piece.sku || l.piece.barcode}</span></div>
          </div>
        </div>
        <div className="card pricepad">
          {phone && (
            <div className="pricepad__piece">
              <span className="pricepad__thumb">{l.piece.image_url ? <img src={thumb(l.piece.image_url, 200)} alt="" /> : null}</span>
              <span className="pricepad__who"><span className="piece__brand">{l.piece.brand}</span><span className="pricepad__title">{l.piece.title}</span><span className="pricepad__meta">{l.piece.sku || l.piece.barcode} · list {fmtC(l.piece.list_cents)}{l.piece.floor_cents != null ? ` · floor ${fmtC(l.piece.floor_cents)}` : ''}</span></span>
            </div>
          )}
          <div className={'pricepad__label' + (phone ? ' pricepad__label--phone' : '')}>Agreed price · as if they were paying cash for it today</div>
          <div className="pricepad__value">{l.price ? fmtC(lineCents(l)) : '$0'}</div>
          <div style={{ minHeight: 22, marginBottom: 8 }}><PriceHint l={l} /></div>
          <NumPad value={edited[step] ? l.price : ''} onChange={setPrice} />
          <div className="pricepad__nav">
            <button type="button" className="btn-secondary" disabled={step === 0} onClick={() => setStep(step - 1)}>Previous</button>
            <button type="button" className="cta cta--center" disabled={lineCents(l) <= 0} onClick={() => step === trade.out.length - 1 ? nav('/trade') : setStep(step + 1)}>{step === trade.out.length - 1 ? 'Back to the trade' : 'Keep, next piece'}</button>
          </div>
        </div>
      </div>
    </Page>
  )
}
